import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, FileText, Package, FileCheck, Settings,
  Menu, X, BarChart3, BoxIcon, Monitor, ClipboardList,
  RotateCcw, Wrench, Users, LogOut, Shield, Truck,
  ChevronDown, Archive, Mail, Ship, Store, Eye, ShoppingBag
} from "lucide-react";
import { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Bell } from "lucide-react";
import { getWholesaleNotifications, markNotificationRead, markAllNotificationsRead } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const allModules = {
  "dashboard":          { path: "/",                    label: "Dashboard",           icon: LayoutDashboard },
  "po":                 { path: "/po",                  label: "PO Processing",       icon: FileText },
  "inventory":          { path: "/inventory",           label: "Inventory",           icon: Package },
  "inventory-legacy":   { path: "/inventory-legacy",    label: "Inventory (Legacy)",  icon: Package },
  "invoices":           { path: "/invoices",            label: "Invoice History",     icon: FileCheck },
  "analytics":          { path: "/analytics",           label: "Revenue Analytics",   icon: BarChart3 },
  "sales-analytics":    { path: "/sales-analytics",     label: "Sales Analytics",     icon: BarChart3 },
  "store-directory":    { path: "/store-directory",      label: "Store Directory",     icon: Store },
  "email":              { path: "/email",                label: "Email Dispatch",      icon: Mail },
  "ap-po":              { path: "/ap/po",               label: "PO Processing",       icon: FileText },
  "ap-inventory":       { path: "/ap/inventory",        label: "Inventory",           icon: Package },
  "ap-invoices":        { path: "/ap/invoices",         label: "Invoice History",     icon: FileCheck },
  "ap-email":           { path: "/ap/email",            label: "Email Dispatch",      icon: Mail },
  "ap-sales-analytics": { path: "/ap/sales-analytics",  label: "Sales Analytics",     icon: BarChart3 },
  "ap-analytics":       { path: "/ap/analytics",        label: "Revenue Analytics",    icon: BarChart3 },
  "ap-store-directory": { path: "/ap/store-directory",   label: "Store Directory",     icon: Store },
  "ap-viewfinder":      { path: "/ap/viewfinder",       label: "Viewfinder",          icon: Eye },
  "ex-po":              { path: "/extreme/po",          label: "PO Processing",       icon: FileText },
  "ex-inventory":       { path: "/extreme/inventory",   label: "Inventory",           icon: Package },
  "ex-invoices":        { path: "/extreme/invoices",    label: "Invoice History",     icon: FileCheck },
  "ex-email":           { path: "/extreme/email",       label: "Email Dispatch",      icon: Mail },
  "ex-sales-analytics": { path: "/extreme/sales-analytics", label: "Sales Analytics", icon: BarChart3 },
  "ex-analytics":       { path: "/extreme/analytics",      label: "Revenue Analytics", icon: BarChart3 },
  "ex-store-directory": { path: "/extreme/store-directory", label: "Store Directory",  icon: Store },
  "packaging":          { path: "/packaging",           label: "Packaging",           icon: BoxIcon },
  "display-kits":       { path: "/display-kits",        label: "Display Kits",        icon: Monitor },
  "inventory-planning": { path: "/inventory-planning",  label: "Inventory Planning",  icon: ClipboardList },
  "rto":                { path: "/rto",                 label: "RTO",                 icon: RotateCcw },
  "repairs":            { path: "/repairs",             label: "Repairs",             icon: Wrench },
  "dispatch-cupboards": { path: "/dispatch-cupboards",  label: "Dispatch Cupboards",  icon: Archive },
  "ops-inventory":      { path: "/ops-inventory",       label: "Inventory",           icon: Package },
  "shipping":           { path: "/shipping",            label: "Shipping Automation", icon: Truck },
  "export-assist":      { path: "/export-assist",       label: "Export Assist",       icon: Ship },
  "settings":           { path: "/settings",            label: "Settings",            icon: Settings },
  "user-management":    { path: "/users",               label: "User Management",     icon: Users, adminOnly: true },
  "wholesale":          { path: "/wholesale/admin",     label: "Wholesale Portal",    icon: ShoppingBag, adminOnly: true, external: true },
};

const defaultGroups = [
  { id: "dashboard",         label: "Dashboard",            icon: LayoutDashboard, modules: ["dashboard"],       collapsible: false },
  { id: "fulfillment",       label: "Fulfillment",          icon: FileText,        modules: [],                  isParent: true,
    children: [
      { id: "fulfillment-oblik", label: "OBLIK",  modules: ["po", "inventory", "invoices", "email", "analytics", "sales-analytics", "store-directory"] },
      { id: "fulfillment-ap",    label: "AP",     modules: ["ap-po", "ap-inventory", "ap-invoices", "ap-email", "ap-analytics", "ap-sales-analytics", "ap-store-directory", "ap-viewfinder"] },
      { id: "fulfillment-extreme", label: "EXTREME", modules: ["ex-po", "ex-inventory", "ex-invoices", "ex-email", "ex-analytics", "ex-sales-analytics", "ex-store-directory"] },
    ]
  },
  { id: "stock-planning",    label: "Stock Planning",       icon: BoxIcon,         modules: ["packaging", "display-kits", "inventory-planning"] },
  { id: "operations",        label: "Operations",           icon: RotateCcw,       modules: ["rto", "repairs", "dispatch-cupboards", "ops-inventory"] },
  { id: "shipping",          label: "Shipping Automation",  icon: Truck,           modules: ["shipping"],        collapsible: false },
  { id: "export-assist",     label: "Export Assist",        icon: Ship,            modules: ["export-assist"],   collapsible: false },
  { id: "admin",             label: "Admin",                icon: Settings,        modules: ["settings", "user-management", "wholesale"] },
];

export const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    const initial = {};
    defaultGroups.forEach(g => { initial[g.id] = true; });
    initial["fulfillment-oblik"] = true;
    initial["fulfillment-ap"] = true;
    initial["fulfillment-extreme"] = true;
    return initial;
  });
  const [hiddenModules, setHiddenModules] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission, isAdmin } = useAuth();

  const loadNotifs = useCallback(() => {
    if (!isAdmin()) return;
    getWholesaleNotifications().then(r => setNotifications(r.data)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => { loadNotifs(); const iv = setInterval(loadNotifs, 30000); return () => clearInterval(iv); }, [loadNotifs]);

  useEffect(() => {
    axios.get(`${API}/menu/settings`).then(res => {
      const data = res.data;
      if (data?.hidden_modules) setHiddenModules(data.hidden_modules);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    for (const group of defaultGroups) {
      if (group.children) {
        for (const child of group.children) {
          const hasActive = child.modules.some(m => allModules[m]?.path === location.pathname);
          if (hasActive) {
            setCollapsed(prev => ({ ...prev, [group.id]: false, [child.id]: false }));
            return;
          }
        }
      } else {
        const hasActive = group.modules.some(m => allModules[m]?.path === location.pathname);
        if (hasActive) {
          setCollapsed(prev => ({ ...prev, [group.id]: false }));
          return;
        }
      }
    }
  }, [location.pathname]);

  const currentPage = useMemo(() => {
    for (const [key, mod] of Object.entries(allModules)) {
      if (mod.path === location.pathname) {
        if (key.startsWith("ap-")) return `AP ${mod.label}`;
        if (key.startsWith("ex-")) return `EXTREME ${mod.label}`;
        return mod.label;
      }
    }
    return "Dashboard";
  }, [location.pathname]);

  const handleLogout = async () => { await logout(); navigate("/"); };
  const toggleGroup = (groupId) => setCollapsed(prev => ({ ...prev, [groupId]: !prev[groupId] }));

  const isModuleVisible = (moduleId) => {
    if (allModules[moduleId]?.adminOnly && !isAdmin()) return false;
    if (hiddenModules.includes(moduleId)) return false;
    return hasPermission(moduleId);
  };

  const renderModuleLink = (moduleId) => {
    const mod = allModules[moduleId];
    if (!mod) return null;
    const Icon = mod.icon;
    if (mod.external) {
      return (
        <a key={moduleId} href={mod.path} onClick={() => setSidebarOpen(false)}
          className="flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-all duration-200 text-slate-400 hover:text-white hover:bg-white/10"
          data-testid={`nav-${moduleId}`}>
          <Icon className="w-4 h-4" />
          <span>{mod.label}</span>
          {mod.adminOnly && <Shield className="w-3 h-3 ml-auto text-purple-400" />}
        </a>
      );
    }
    return (
      <NavLink key={moduleId} to={mod.path} onClick={() => setSidebarOpen(false)}
        className={({ isActive }) => `
          flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-all duration-200
          ${isActive ? 'bg-blue-600/80 text-white' : 'text-slate-400 hover:text-white hover:bg-white/10'}
        `} data-testid={`nav-${moduleId}`}>
        <Icon className="w-4 h-4" />
        <span>{mod.label}</span>
        {mod.adminOnly && <Shield className="w-3 h-3 ml-auto text-purple-400" />}
      </NavLink>
    );
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-slate-900 text-white z-50
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
      `}>
        <div className="h-16 flex items-center px-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-sm flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-lg tracking-tight font-heading">InvoiceFlow</span>
          </div>
          <button className="ml-auto md:hidden text-slate-400 hover:text-white" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-3 space-y-0.5 max-h-[calc(100vh-180px)] overflow-y-auto" data-testid="sidebar-nav">
          {defaultGroups.map((group) => {
            // For parent groups with children (Fulfillment -> OBLIK, AP)
            if (group.isParent && group.children) {
              const allChildModules = group.children.flatMap(c => c.modules);
              const visibleChildModules = allChildModules.filter(isModuleVisible);
              if (visibleChildModules.length === 0) return null;

              const isOpen = !collapsed[group.id];
              const hasActiveChild = allChildModules.some(m => allModules[m]?.path === location.pathname);
              const GroupIcon = group.icon;

              return (
                <div key={group.id} className="mb-1">
                  <button onClick={() => toggleGroup(group.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200
                      ${hasActiveChild ? 'text-blue-300' : 'text-slate-300 hover:text-white hover:bg-white/5'}`}
                    data-testid={`nav-group-${group.id}`}>
                    <GroupIcon className="w-4.5 h-4.5" />
                    <span>{group.label}</span>
                    <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`} />
                  </button>

                  {isOpen && group.children.map((child) => {
                    const visibleMods = child.modules.filter(isModuleVisible);
                    if (visibleMods.length === 0) return null;
                    const childOpen = !collapsed[child.id];
                    const childActive = visibleMods.some(m => allModules[m]?.path === location.pathname);

                    return (
                      <div key={child.id} className="ml-3 pl-2 border-l border-slate-700/50 mt-0.5">
                        <button onClick={() => toggleGroup(child.id)}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition-all duration-200
                            ${childActive ? 'text-blue-300' : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'}`}
                          data-testid={`nav-group-${child.id}`}>
                          <span>{child.label}</span>
                          <ChevronDown className={`w-3 h-3 ml-auto transition-transform duration-200 ${childOpen ? '' : '-rotate-90'}`} />
                        </button>
                        {childOpen && (
                          <div className="ml-2 pl-2 border-l border-slate-700/30 mt-0.5 space-y-0.5">
                            {visibleMods.map(renderModuleLink)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            }

            // Standard groups (non-parent)
            const visibleModules = group.modules.filter(isModuleVisible);
            if (visibleModules.length === 0) return null;

            const isSingle = group.collapsible === false || visibleModules.length === 1;
            const isOpen = !collapsed[group.id];
            const hasActiveChild = visibleModules.some(m => allModules[m]?.path === location.pathname);
            const GroupIcon = group.icon;

            if (isSingle && visibleModules.length === 1) {
              const mod = allModules[visibleModules[0]];
              const Icon = mod.icon;
              return (
                <NavLink key={group.id} to={mod.path} onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200
                    ${isActive ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-white/10'}
                  `} data-testid={`nav-${visibleModules[0]}`}>
                  <Icon className="w-4.5 h-4.5" />
                  <span>{mod.label}</span>
                  {mod.adminOnly && <Shield className="w-3 h-3 ml-auto text-purple-400" />}
                </NavLink>
              );
            }

            return (
              <div key={group.id} className="mb-1">
                <button onClick={() => toggleGroup(group.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200
                    ${hasActiveChild ? 'text-blue-300' : 'text-slate-300 hover:text-white hover:bg-white/5'}`}
                  data-testid={`nav-group-${group.id}`}>
                  <GroupIcon className="w-4.5 h-4.5" />
                  <span>{group.label}</span>
                  <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`} />
                </button>
                {isOpen && (
                  <div className="ml-4 pl-3 border-l border-slate-700/50 mt-0.5 space-y-0.5">
                    {visibleModules.map(renderModuleLink)}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
          {user && (
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${user.role === 'admin' ? 'bg-purple-500' : 'bg-slate-600'}`}>
                  {user.display_name?.charAt(0)?.toUpperCase() || "U"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user.display_name}</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1">
                    {user.role === 'admin' && <Shield className="w-3 h-3" />}
                    {user.role}
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm"
                className="w-full justify-start text-slate-400 hover:text-white hover:bg-white/10"
                onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          )}
          <div className="text-xs text-slate-500">
            <p>VARDHMAN MARKETING</p>
            <p className="mt-1">v2.0.0</p>
          </div>
        </div>
      </aside>

      <div className="flex-1 md:ml-64">
        <header className="h-16 bg-white border-b border-slate-200 sticky top-0 z-30 glass-header">
          <div className="h-full px-4 md:px-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" className="md:hidden"
                onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
              <h1 className="text-lg font-semibold text-slate-900 font-heading" data-testid="page-title">
                {currentPage}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {isAdmin() && (
                <div className="relative">
                  <button onClick={() => setNotifOpen(!notifOpen)} className="relative p-2 text-slate-400 hover:text-slate-600 transition-colors" data-testid="vm-notification-bell">
                    <Bell className="w-5 h-5" />
                    {notifications.filter(n => !n.read).length > 0 && (
                      <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                        {notifications.filter(n => !n.read).length}
                      </span>
                    )}
                  </button>
                  {notifOpen && <>
                    <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                    <div className="absolute right-0 top-10 w-80 max-h-96 bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden flex flex-col">
                      <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center">
                        <span className="text-xs font-semibold tracking-wider uppercase text-slate-500">Notifications</span>
                        {notifications.filter(n => !n.read).length > 0 && (
                          <button onClick={() => { markAllNotificationsRead().catch(() => {}); setNotifications(prev => prev.map(n => ({...n, read: true}))); }} className="text-[10px] text-blue-600 hover:text-blue-800 font-medium">Mark all read</button>
                        )}
                      </div>
                      <div className="flex-1 overflow-y-auto max-h-80">
                        {notifications.length === 0 && <div className="p-6 text-center text-slate-400 text-xs">No notifications</div>}
                        {notifications.map(n => (
                          <div key={n.id} onClick={() => { if (!n.read) { markNotificationRead(n.id).catch(() => {}); setNotifications(prev => prev.map(x => x.id === n.id ? {...x, read: true} : x)); } }}
                            className={`px-4 py-3 border-b border-slate-50 cursor-pointer hover:bg-slate-50 ${n.read ? '' : 'bg-blue-50/50'}`}>
                            <div className="text-xs font-semibold text-slate-800">{n.title}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5">{n.body}</div>
                            <div className="text-[10px] text-slate-300 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>}
                </div>
              )}
              <span className="text-sm text-slate-500 hidden sm:block">VARDHMAN MARKETING</span>
            </div>
          </div>
        </header>
        <main className="p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
